import type {
  AllCustomerDetailOpenPosition,
  AllCustomerDetailPosition,
  BPWarengruppe,
  Bestellvorschlag,
  BestellvorschlagEntry,
  EKStuecklisteDto,
  EKStuecklisteWithDetailsDto,
} from "@/shared-types";
import { formatDateTime } from "./dateUtil";
import { toast } from "sonner";
import { TypeByCategory, TypeByArticle } from "../components/analyze-customer/customer-page";

const internalAPIPath = "/api/internal";

export async function lagerauswertungAlle(limitDate: string | undefined) {
  const result = await fetch(
    `${internalAPIPath}/lagerauswertung/alle${limitDate ? `/${limitDate}` : ""}`,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("sessionToken"),
      },
    }
  );
  return await result.json();
}

export async function lagerauswertung() {
  const result = await fetch(`${internalAPIPath}/lagerauswertung`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function listAll() {
  const result = await fetch(`${internalAPIPath}/list`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function inconsistentOrders() {
  const result = await fetch(`${internalAPIPath}/inconsistentOrders`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function listAll2() {
  const res = await fetch(`/api/list2`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    signal: AbortSignal.timeout(120_000),
  });
  const response = (await res.json()) as BPWarengruppe[];
  return response;
}

export async function createVorschlag(body: any) {
  const json = await fetch(`${internalAPIPath}/vorschlag`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(body),
  });
  const response = (await json.json()) as { id: number };
  console.log(response);
  return response.id;
}

export async function removeVorschlag(id: number) {
  const json = await fetch(`${internalAPIPath}/vorschlag/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  const response = await json.json();
  console.log(response);
}

export async function updateVorschlag(body: any) {
  const json = await fetch(`${internalAPIPath}/vorschlag`, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(body),
  });
  const response = await json.json();
  console.log(response);
}

export async function allVorschlaege() {
  const json = await fetch(`${internalAPIPath}/vorschlag`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  const response = await json.text();
  return JSON.parse(response, (key, value) => {
    return key === "createdAt" ? new Date(value) : value;
  }) as Bestellvorschlag[];
}

export async function vorschlagEntries(id: number) {
  const json = await fetch(`${internalAPIPath}/vorschlag/entries/${id}`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  const response = await json.text();
  return JSON.parse(response, (key, value) => {
    return key === "createdAt" ? new Date(value) : value;
  }) as BestellvorschlagEntry[];
}

export function odooImportVorschlag(
  setLoading: (loading: boolean) => void,
  activeVorschlag: Bestellvorschlag | undefined
) {
  if (!activeVorschlag) return;
  setLoading(true);
  fetch(`/api/odoo-import/${activeVorschlag.id}`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  })
    .then((response) => response.json())
    .then((res) => {
      console.log(res);
      setLoading(false);
    });
}

export function exportAndDownloadVorschlag(
  setLoading: (loading: boolean) => void,
  activeVorschlag: Bestellvorschlag | undefined
) {
  if (!activeVorschlag) return;
  setLoading(true);
  fetch(`${internalAPIPath}/excel-export/${activeVorschlag.id}`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Vorschlag_${activeVorschlag.name.replace(
          /[^a-z0-9]/gi,
          ""
        )}_${formatDateTime(new Date()).replace(", ", "_")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setLoading(false);
    });
}

export async function exportAndDownloadAbzugBremen(
  setLoading: (loading: boolean) => void,
  list: { ArtNr: string; Bestellnummer: string; amount: number; sumKosten: number }[]
) {
  try {
    if (!list) return;
    if (list.length === 0) {
      toast.error("Keine Artikel zum Exportieren");
      return;
    }
    const response = await fetch(`${internalAPIPath}/excel-export-abzug-bremen`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("sessionToken"),
      },
      body: JSON.stringify(list),
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Abzug_Bremen_${formatDateTime(new Date()).replace(", ", "_")}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  } finally {
    setLoading(false);
  }
}

export async function user() {
  const json = await fetch(`${internalAPIPath}/user`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  if (json.status === 401) {
    throw new Error("Unauthorized");
  }
  const response = (await json.json()) as { name: string; email: string };
  return response;
}

export async function blUpdateExclude(
  number: string,
  toUpdate: {
    excludeFromExport?: boolean;
    dueDate?: string | null;
    confirmationName?: string;
    confirmationDate?: string;
    confirmationFile?: string;
  }
) {
  await fetch(`/api/bl`, {
    method: "PUT",
    body: JSON.stringify({ number, ...toUpdate }),
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
}

export function exportAndDownloadBl(setLoading: (loading: boolean) => void) {
  setLoading(true);
  fetch(`/api/bl/excel-export`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BL_${formatDateTime(new Date()).replace(", ", "_")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setLoading(false);
    });
}

export function createPrioList(name: string) {
  return fetch(`/api/bl/priolist`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify({ name }),
  });
}

export function changePrioList(payload: { id: number; name: string }) {
  return fetch(`/api/bl/priolist`, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(payload),
  });
}

export function deletePrioList(id: number) {
  return fetch(`/api/bl/priolist`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify({ id }),
  });
}

export function addToPrioList(payload: { id: number; number: string }) {
  return fetch(`/api/bl/priolist/item`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(payload),
  });
}

export function removeFromPrioList(payload: { id: number; number: string }) {
  return fetch(`/api/bl/priolist/item`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(payload),
  });
}

export async function orderList() {
  const result = await fetch(`/api/orders`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function allCustomer() {
  const result = await fetch(`${internalAPIPath}/all-customer`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function allCustomerDetail() {
  const result = await fetch(`${internalAPIPath}/all-customer-detail`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function downloadExcelOrders(
  setLoading: (loading: boolean) => void,
  open: AllCustomerDetailOpenPosition[],
  kundennummer: string
) {
  setLoading(true);
  const response = await fetch(`${internalAPIPath}/all-customer-detail/excel-orders`, {
    method: "POST",
    body: JSON.stringify(open),
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });

  const blob = await response.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `orders_${kundennummer}_${formatDateOnly(new Date()).replace(", ", "_")}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  setLoading(false);
}

export async function downloadExcelCategory(
  setLoading: (loading: boolean) => void,
  data: TypeByCategory[],
  kundennummer: string
) {
  setLoading(true);
  const response = await fetch(`${internalAPIPath}/all-customer-detail/excel-category`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  const blob = await response.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `verkaufe_nach_kategory_${kundennummer}_${formatDateOnly(new Date()).replace(", ", "_")}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  setLoading(false);
}

export async function downloadExcelArticle(
  setLoading: (loading: boolean) => void,
  data: TypeByArticle[],
  kundennummer: string
) {
  setLoading(true);
  const response = await fetch(`${internalAPIPath}/all-customer-detail/excel-article`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  const blob = await response.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `verkaufe_nach_artikel_${kundennummer}_${formatDateOnly(new Date()).replace(", ", "_")}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  setLoading(false);
}

export async function downloadExcelVariant(
  setLoading: (loading: boolean) => void,
  data: AllCustomerDetailPosition[],
  kundennummer: string
) {
  setLoading(true);
  const response = await fetch(`${internalAPIPath}/all-customer-detail/excel-variant`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  const blob = await response.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `verkaufe_nach_variant_${kundennummer}_${formatDateOnly(new Date()).replace(", ", "_")}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  setLoading(false);
}

function formatDateOnly(date: Date) {
  const timeFormat: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  return date.toLocaleString("de-DE", timeFormat);
}

export async function prognose() {
  const result = await fetch(`${internalAPIPath}/prognose`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function productsStueckliste(): Promise<{
  mainProducts: {
    id: number;
    name: string;
    x_maiva_jd_product_template: string;
    default_code: string;
  }[];
  stuecklisteProducts: {
    id: number;
    name: string;
    x_maiva_jd_product_template: string | false;
    default_code: string;
  }[];
}> {
  const result = await fetch(`${internalAPIPath}/products-stueckliste`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function ekStueckliste(): Promise<EKStuecklisteDto[]> {
  const result = await fetch(`${internalAPIPath}/ek-stueckliste`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function ekStuecklisteByTemplateId(
  templateId: number
): Promise<EKStuecklisteWithDetailsDto | null> {
  const result = await fetch(`${internalAPIPath}/ek-stueckliste/template/${templateId}`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function createStueckliste(body: Omit<EKStuecklisteDto, "id">) {
  const result = await fetch(`${internalAPIPath}/ek-stueckliste`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(body),
  });
  return await result.json();
}

export async function updateStueckliste(body: EKStuecklisteDto) {
  const result = await fetch(`${internalAPIPath}/ek-stueckliste`, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
    body: JSON.stringify(body),
  });
  return await result.json();
}

export async function deleteStueckliste(id: number) {
  const result = await fetch(`${internalAPIPath}/ek-stueckliste/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    },
  });
  return await result.json();
}

export async function importExcelLagerBremen(setLoading: (loading: boolean) => void, file: File) {
  setLoading(true);

  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${internalAPIPath}/excel-import`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("sessionToken"),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error from api frontend by importExcelLagerBremen:", errorText);
      throw new Error(errorText);
    }

    const result = await response.json();
    console.log("Import result:", result);
    setLoading(false);
  } catch (error) {
    console.error("Error during Excel importin importExcelLagerBremen:", error);
    setLoading(false);
    throw error;
  }
}
